import {GLOBAL} from "../../../common/globals";
import {resolve} from "../../../container";
import {customElement, property, query} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {ManagingResources} from "../../../common/lifetime";
import {FeatureDetector} from "../../../common/featureDetector";
import type {Nullable} from "../../../bootstrap/common/types";
import {Dictionary} from "../dictionary";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";
import {UnLitElement, WithEopSlots} from "../../../common/elements";
import {Resolution} from "../../../common/resolution";
import {Page} from "../../../common/page";
import {eventOccurredOutside} from "../../../common/utils/events";
import {BOOTSTRAP} from "../../../common/resolutionConstants";
import {noop} from "../../../common/utils/functions";

const POPOVER_BACKGROUNDS: Record<string, string> = {
    grayStronger: "popover-color-gray-stronger"
};
const ADDITIONAL_TOOLTIP_PADDING_MOBILE = 10;
const ADDITIONAL_TOOLTIP_PADDING_DESKTOP = 25;
const POPOVER_ABOVE_CLASS = "popover-above";
const POPOVER_BELOW_CLASS = "popover-below";
const POPOVER_ON_LEFT_EDGE_CLASS = "popover-on-left-edge";
const POPOVER_ON_RIGHT_EDGE_CLASS = "popover-on-right-edge";
const POPOVER_IN_CENTER = "popover-in-center";
const POPOVER_ARROW_HEIGHT = 11;

type AnchorMetrics = {
    anchorHeight: number;
    anchorWidth: number;
    anchorTop: number;
    anchorLeft: number;
    anchorRight: number;
};

type PopoverMetrics = {
    popoverHeight: number;
    popoverWidth: number;
    additionalPadding: number;
};

@customElement("eop-popover")
export class EopPopover extends WithEopSlots(ManagingResources(UnLitElement)) {

    @query(".popover-container")
    public popoverContainer: HTMLElement;
    @query(".popover")
    public popoverElement: HTMLElement;
    @query(".close-button")
    public popoverCloser: HTMLElement;
    @property({attribute: "enforce-close-button", type: Boolean})
    public enforceCloseButton: boolean = false;
    @property({attribute: "popover-id"})
    public popoverId: string;

    @property({attribute: "background"})
    public background: string;

    private dictionary: Dictionary;
    private outsideClickHandler: ((event: Event) => void) = noop;
    private windowResizeHandler: (() => void) = noop;

    public constructor(
        private features: FeatureDetector = resolve(FeatureDetector),
        private resolution: Resolution = resolve(Resolution),
        private page: Page = resolve(Page)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.resolution.onWindowResize(() => this.windowResizeHandler());
    }

    public render(): TemplateResult {
        return html`
            <div class="popover-container">
                <div popover class="popover rich-text">
                    <div class="popover-bubble ${POPOVER_BACKGROUNDS[this.background]}">
                        ${this.renderCloseButton()}
                        ${this.resolveSlot(this.popoverId)}
                    </div>
                </div>
            </div>
        `;
    }

    public renderCloseButton(): Nullable<TemplateResult> {
        if (this.enforceCloseButton || this.features.isTouchDevice()) {
            return html`
                <button type="button"
                        class="close-button"
                        aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")}
                        ${trackingButtonInteract("popover-close-button")}
                        @click=${() => this.close()}
                ><eop-svg-sprite-icon icon-id="close"></eop-svg-sprite-icon></button>`;
        } else {
            return null;
        }
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
    }

    public doHide(): void {
        this.popoverElement.hidePopover();
    }

    public open(anchor: HTMLElement): void {
        this.removeOutsideClickHandler();
        this.outsideClickHandler = event => {
            if (eventOccurredOutside(event, anchor) && eventOccurredOutside(event, this.popoverElement)) {
                this.close();
            }
            event.stopPropagation();
        };
        this.windowResizeHandler = () => this.adjustPopoverOffset(anchor);

        this.popoverElement.showPopover();
        this.adjustPopoverOffset(anchor);

        GLOBAL.bodyElement().addEventListener("click", this.outsideClickHandler);
        GLOBAL.bodyElement().addEventListener("touchstart", this.outsideClickHandler);
        this.popoverElement.addEventListener("click", this.outsideClickHandler);
        this.popoverElement.addEventListener("touchstart", this.outsideClickHandler);
    }

    public close(): void {
        this.removeOutsideClickHandler();
        this.doHide();
    }

    private removeOutsideClickHandler(): void {
        if (this.outsideClickHandler === noop) {
            return;
        }
        GLOBAL.bodyElement().removeEventListener("click", this.outsideClickHandler);
        GLOBAL.bodyElement().removeEventListener("touchstart", this.outsideClickHandler);
        this.popoverElement.removeEventListener("click", this.outsideClickHandler);
        this.popoverElement.removeEventListener("touchstart", this.outsideClickHandler);
        this.outsideClickHandler = noop;
    }

    public toggle(anchor: HTMLElement): void {
        if (!this.popoverElement.hasAttribute("open")) {
            this.open(anchor);
        } else {
            this.close();
        }
    }

    private adjustPopoverOffset(anchor: HTMLElement): void {
        this.resetLayout();
        const anchorMetrics = this.anchorMetrics(anchor);
        const popoverMetrics = this.popoverMetrics();
        this.updateCssVariables(anchorMetrics, popoverMetrics);
        this.updatePositioningClasses(anchorMetrics, popoverMetrics);
    }

    private anchorMetrics(anchor: HTMLElement): AnchorMetrics {
        const {width: anchorWidth, height: anchorHeight} = anchor.getBoundingClientRect();
        return {
            anchorHeight,
            anchorWidth,
            anchorTop: anchor.topOffset(),
            anchorLeft: anchor.leftOffset(),
            anchorRight: anchor.rightOffset()
        };
    }

    private popoverMetrics(): PopoverMetrics {
        return {
            popoverHeight: this.popoverElement.clientHeight,
            popoverWidth: this.popoverElement.clientWidth,
            additionalPadding: this.resolution.upTo(BOOTSTRAP.XS)
                ? ADDITIONAL_TOOLTIP_PADDING_MOBILE
                : ADDITIONAL_TOOLTIP_PADDING_DESKTOP
        };
    }

    private updateCssVariables(anchorMetrics: AnchorMetrics, popoverMetrics: PopoverMetrics): void {
        const anchorHeight = this.style.getPropertyValue("--anchor-height");
        const anchorWidth = this.style.getPropertyValue("--anchor-width");
        if (anchorHeight === "0px" || anchorWidth === "0px" || anchorHeight === "" || anchorWidth === "") {
            this.style.setProperty("--anchor-height", `${anchorMetrics.anchorHeight}px`);
            this.style.setProperty("--anchor-width", `${anchorMetrics.anchorWidth}px`);
        }
        this.style.setProperty("--anchor-center-top-offset", `${anchorMetrics.anchorTop + 0.5 * anchorMetrics.anchorHeight}px`);
        this.style.setProperty("--anchor-center-left-offset", `${anchorMetrics.anchorLeft + 0.5 * anchorMetrics.anchorWidth}px`);
        this.style.setProperty("--anchor-center-right-viewport-offset", `${this.page.viewportWidth() - anchorMetrics.anchorRight + 0.5 * anchorMetrics.anchorWidth}px`);
        this.popoverContainer.style.setProperty("--additional-padding", `${popoverMetrics.additionalPadding}px`);
        this.popoverContainer.style.setProperty("--popover-height", `${popoverMetrics.popoverHeight}px`);
    }

    private resetLayout(): void {
        [
            POPOVER_ABOVE_CLASS,
            POPOVER_BELOW_CLASS,
            POPOVER_IN_CENTER,
            POPOVER_ON_LEFT_EDGE_CLASS,
            POPOVER_ON_RIGHT_EDGE_CLASS
        ].forEach(className => this.popoverContainer.classList.remove(className));
        this.popoverElement.style.left = "";
    }

    private updatePositioningClasses(anchorMetrics: AnchorMetrics, popoverMetrics: PopoverMetrics): void {
        const minimumDistanceToEdge = popoverMetrics.additionalPadding + popoverMetrics.popoverWidth * 0.5;
        if (anchorMetrics.anchorLeft < minimumDistanceToEdge) {
            this.popoverContainer.classList.add(POPOVER_ON_LEFT_EDGE_CLASS);
        } else if (this.page.viewportWidth() - anchorMetrics.anchorLeft < minimumDistanceToEdge) {
            this.popoverContainer.classList.add(POPOVER_ON_RIGHT_EDGE_CLASS);
        } else {
            this.popoverContainer.classList.add(POPOVER_IN_CENTER);
        }
        if (anchorMetrics.anchorTop - this.page.getEffectiveYScrollPosition() - popoverMetrics.popoverHeight - POPOVER_ARROW_HEIGHT <= 0) {
            this.popoverContainer.classList.add(POPOVER_BELOW_CLASS);
        } else {
            this.popoverContainer.classList.add(POPOVER_ABOVE_CLASS);
        }
    }
}